import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

const inputVariants = cva(
  'flex w-full rounded-lg border bg-white px-3 py-2 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100',
  {
    variants: {
      variant: {
        default: 'border-gray-300 focus-visible:ring-blue-600 focus-visible:border-blue-600',
        error: 'border-red-500 focus-visible:ring-red-600 focus-visible:border-red-600',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  loading?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant,
      type,
      iconLeft,
      iconRight,
      loading = false,
      disabled,
      ...props
    },
    ref
  ) => {
    if (!iconLeft && !iconRight && !loading) {
      return (
        <input
          type={type}
          className={twMerge(inputVariants({ variant }), className)}
          ref={ref}
          disabled={disabled || loading}
          {...props}
        />
      );
    }

    return (
      <div className="relative w-full">
        {iconLeft && (
          <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true">
            {iconLeft}
          </div>
        )}
        <input
          type={type}
          className={twMerge(
            inputVariants({ variant }),
            iconLeft && 'pl-10',
            (iconRight || loading) && 'pr-10',
            className
          )}
          ref={ref}
          disabled={disabled || loading}
          aria-disabled={disabled || loading}
          aria-invalid={variant === 'error'}
          {...props}
        />
        {loading && (
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
        {!loading && iconRight && (
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true">
            {iconRight}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
